import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  faMessageDots,
  faXmark,
  faPlaneDeparture,
  faTrain,
  faShieldCheck,
  faMagnifyingGlass,
  faPenToSquare,
  faPaperPlaneTop,
  faCircleCheck,
  faPlus,
  faMicrophone,
  faMicrophoneSlash,
  faLocationDot,
  faCalendarDays,
  faChevronRight,
  faArrowLeft,
  faThumbsUp,
  faThumbsDown,
  faHotel,
  faCarSide,
  faBasketShopping,
  faCocktail,
  IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import {
  BotFaq,
  ChatCarHireResultCard,
  ChatFastTrackResultCard,
  ChatLoungeResultCard,
  ChatMessage,
  ChatMiAnswer,
  ChatParkingResultCard,
  ChatRailResultCard,
  ChatRailResults,
  ChatRailReturnPrompt,
  ChatSearchParameters,
  ChatSuggestion,
  ChatTokenEvent,
  BookingInfo,
  BookingInfoPassenger,
  PaymentMethodPrompt,
  ApproverSelectionPrompt,
  MyBookingsBaseParams,
  BookingSummaryItem
} from '@sabstravtech/obtservices/base';
import {
  ChatbotService,
  ResultAwareService,
  UserService,
  ChatbotHotelSearchService,
  ChatbotTaxiSearchService,
  ServiceType,
  RailTicketExtra
} from '@sabstravtech/obtservices/angular';
import { ChatTaxiResultCard } from '@sabstravtech/obtservices/base';
import { feedbackReasonsForRating } from './chat-feedback-reasons';
import { ChatFlightFareSelection } from './chat-flight-card/chat-flight-card.component';
import { ChatHotelRoomSelection } from './chat-hotel-card/chat-hotel-card.component';
import { ChatEurostarFareSelection } from './chat-eurostar-card/chat-eurostar-card.component';
import { ChatPhoneAnswer } from './chat-phone-input/chat-phone-input.component';
import { ChatPassportAnswer } from './chat-passport-input/chat-passport-input.component';
import { ChatLoyaltyAnswer } from './chat-loyalty-input/chat-loyalty-input.component';
import { ChatChoice, ChatChoiceAnswer } from './chat-choice-input/chat-choice-input.component';
import { ChatEmailAnswer } from './chat-email-input/chat-email-input.component';
import { ChatFlightDetailsAnswer } from './chat-flight-details-input/chat-flight-details-input.component';
import { ChatDriverNotesAnswer } from './chat-driver-notes-input/chat-driver-notes-input.component';
import { ChatFlightNumberAnswer } from './chat-flight-number-input/chat-flight-number-input.component';
import { ChatAddressAnswer } from './chat-address-input/chat-address-input.component';
import {
  ChatPaymentMethodAnswer,
  ChatPaymentMethodChoice
} from './chat-payment-input/chat-payment-input.component';
import {
  ChatApproverAnswer,
  ChatApproverChoice
} from './chat-approver-input/chat-approver-input.component';

import { VoiceRecognitionService } from '../../services/voice-recognition.service';
import { ChatCarDetailsAnswer } from './chat-car-details-input/chat-car-details-input.component';
import { ChatJourneyDetailsAnswer } from './chat-journey-details-input/chat-journey-details-input.component';

export interface RailResultsSummary {
  summaryDate: string;
  cheapestReturnPrice: number | null;
  cheapestSinglePrice: number | null;
}

interface BookingSummaryItemDisplay {
  icon: IconDefinition;
  productText: string;
  dateText: string;
  listCarriers?: string;
}

type RailSeatReservationMessage = ChatMessage & {
  railSeatReservationPrompt?: {
    basketId: string;
    itemLabel?: string;
    seats: string[];
    journeys?: Array<{ direction?: string; route?: string; seats: string[] }>;
  };
};

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
  standalone: false
})
export class ChatWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageList') messageList!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;
  readonly ServiceType = ServiceType;
  isOpen = false;
  inputText = '';
  messages: RailSeatReservationMessage[] = [];
  sameAddressChoices = new Set<string>();
  isLoading = false;
  isSearching = false;
  searchingTravelType?: string;
  isEnabled = false;
  userForename = '';
  userEmail = '';

  // Red notification dot on the launcher when an upcoming cancellable rail journey
  // prompt is waiting (seeded but not yet seen by the user).
  hasUnreadPrompt = false;

  // Guard so the startup rail-journey check fires only once even though
  // allDetails can emit repeatedly.
  private checkedRailPrompt = false;

  // ENT-13896 — voice input state (mirrors mobile chat widget).
  isVoiceSupported = false;
  isListening = false;

  // Header button tooltip state
  tooltipVisible = false;
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;

  readonly newChatLabel = $localize`:@@chatWidgetNewChat:New chat`;
  readonly closeChatLabel = $localize`:@@chatWidgetCloseChat:Close chat`;
  readonly listeningPlaceholder = $localize`:@@chatWidgetListeningPlaceholder:Listening…`;
  readonly askAnythingPlaceholder = $localize`:@@chatWidgetAskAnythingPlaceholder:Ask anything...`;
  readonly stopListeningLabel = $localize`:@@chatWidgetStopListening:Stop listening`;
  readonly voiceInputLabel = $localize`:@@chatWidgetVoiceInput:Voice input`;

  faMessageDots = faMessageDots;
  faXmark = faXmark;
  faPlaneDeparture = faPlaneDeparture;
  faTrain = faTrain;
  faHotel = faHotel;
  faShieldCheck = faShieldCheck;
  faMagnifyingGlass = faMagnifyingGlass;
  faPenToSquare = faPenToSquare;
  faPaperPlaneTop = faPaperPlaneTop;
  faCircleCheck = faCircleCheck;
  faPlus = faPlus;
  faMicrophone = faMicrophone;
  faMicrophoneSlash = faMicrophoneSlash;
  faLocationDot = faLocationDot;
  faCalendarDays = faCalendarDays;
  faChevronRight = faChevronRight;
  faArrowLeft = faArrowLeft;
  faThumbsUp = faThumbsUp;
  faThumbsDown = faThumbsDown;

  formattedTextMap: Record<number, string> = {};
  railSummaryMap: Record<number, RailResultsSummary> = {};
  eurostarSummaryDateMap: Record<number, string> = {};
  bookingSummaryDisplay: Record<string, BookingSummaryItemDisplay> = {};
  bookingSelectionFlags: Record<string, { isFlight: boolean; isRail: boolean }> = {};

  // ENT-16320 — browse-and-select FAQ landing view (no AI).
  readonly faqPreviewCount = 5;
  faqs: BotFaq[] = [];
  faqExpanded = false;
  selectedFaq: BotFaq | null = null;
  selectedFaqAnswerHtml = '';

  // ENT-16247 — keyed by ChatMessage.id; only assistant messages get rated.
  feedbackRatings: Record<string, 'up' | 'down'> = {};
  feedbackReasons: Record<string, string> = {};
  feedbackComments: Record<string, string> = {};
  feedbackConfirmed: Record<string, boolean> = {};
  feedbackDetailOpenFor: string | null = null;
  // ENT-16247 — chips shown in the currently-open feedback panel. Derived
  // in rateMessage() from the active rating so the template doesn't have
  // to call a getter on every change-detection cycle.
  activeFeedbackReasons: readonly string[] = [];

  private bookingAcknowledgements: Record<
    string,
    { started: boolean; terms: boolean; identity: boolean; final: boolean; confirming?: boolean }
  > = {};
  private completedBookingInfoPassengers: Record<string, Set<string>> = {};
  private bookingInfoPassengerSelections = new WeakMap<BookingInfo, BookingInfoPassenger>();
  private railSeatReservationContinues = new Set<string>();
  // Localized labels for the upcoming rail cancellation buttons
  readonly cancelActionLabels: Record<string, string> = {
    KEEP_RAIL_JOURNEY: $localize`:@@chatKeepRailJourney:Yes, this rail journey is still required`,
    CANCEL_RAIL_OUTBOUND: $localize`:@@chatCancelRailOutbound:No, cancel outbound only`,
    CANCEL_RAIL_INBOUND: $localize`:@@chatCancelRailInbound:No, cancel return only`,
    CANCEL_RAIL_JOURNEY: $localize`:@@chatCancelRailJourney:No, cancel my booking`
  };

  // Chat tooltip text, chosen by unread state. Static strings routed through
  // Angular i18n ($localize); the conditional binding can't carry an inline
  // i18n-attr, so they live as component fields.
  readonly chatTooltipNewMessage = $localize`:@@chatTooltipNewMessage:You have a new message`;
  readonly chatTooltipGreeting = $localize`:@@chatTooltipGreeting:Hello – have a question? Let's Chat.`;

  private subscriptions = new Subscription();
  /** ENT-18234 - the return-leg question currently on screen, or null. */
  activeRailReturnPrompt: ChatRailReturnPrompt | null = null;
  /** ENT-18234 - id of the newest message carrying rail results. Older searches keep
   *  their cards on screen and card index alone would match those too. */
  latestRailResultsMessageId = '';

  private shouldScrollToBottom = false;
  private currentTokenBuffer = '';

  constructor(
    private chatbotService: ChatbotService,
    private resultAwareService: ResultAwareService,
    private userService: UserService,
    private voiceRecognition: VoiceRecognitionService,
    private router: Router,
    private chatbotHotelSearchService: ChatbotHotelSearchService,
    private chatbotTaxiSearchService: ChatbotTaxiSearchService
  ) {}

  ngOnInit(): void {
    this.isVoiceSupported = this.voiceRecognition.isSupported();

    this.subscriptions.add(
      this.userService.fullUserDetails.allDetails.subscribe((user) => {
        this.isEnabled = !!user?.aiTravelAssistantEnabled;
        this.userForename = user?.forename;
        this.userEmail = user?.email ?? '';

        if (this.isEnabled && !this.checkedRailPrompt) {
          this.checkedRailPrompt = true;
          // Fire-and-forget over the websocket (opens the first socket if needed).
          // The response is handled asynchronously via unreadPrompt$ below.
          this.chatbotService.checkNextCancellableRailJourneys({ withinDays: 2 });
        }
      })
    );

    this.subscriptions.add(
      // Surface a notification dot on the launcher instead of forcing the panel
      // open. The prompt message is already seeded into the chat history, so it
      // renders as soon as the user opens the widget.
      this.chatbotService.unreadPrompt$.subscribe(() => {
        this.hasUnreadPrompt = true;
      })
    );

    this.subscriptions.add(
      this.chatbotService.messages$.subscribe((messages) => {
        this.messages = messages;
        this.formattedTextMap = {};
        this.railSummaryMap = {};
        this.eurostarSummaryDateMap = {};
        this.bookingSummaryDisplay = {};
        this.bookingSelectionFlags = {};
        let latestRailResultsMessageId = '';
        messages.forEach((msg, i) => {
          if (msg.role === 'assistant') {
            this.formattedTextMap[i] = this.formatMarkdown(msg.text);
          }
          if (msg.railResults) {
            this.railSummaryMap[i] = this.buildRailSummary(msg.railResults);
            latestRailResultsMessageId = msg.id || '';
          }
          if (msg.eurostarResults) {
            this.eurostarSummaryDateMap[i] = this.formatDate(
              msg.eurostarResults.parameters.departureDate
            );
          }
          this.cacheBookingSummaryDisplay(msg);
          this.cacheBookingSelectionFlags(msg);
        });
        this.latestRailResultsMessageId = latestRailResultsMessageId;
        this.shouldScrollToBottom = true;
      })
    );

    // ENT-18234 - the second-leg question lives on the service and renders inside the
    // rail card the fare was clicked on, so it does not arrive as a chat message.
    this.subscriptions.add(
      this.resultAwareService.railReturnPrompt$.subscribe((prompt) => {
        this.activeRailReturnPrompt = prompt;
      })
    );

    this.subscriptions.add(
      this.chatbotService.loading$.subscribe((loading) => {
        this.isLoading = loading;
        if (loading) {
          // ENT-16431 — the typing indicator renders inside the scrollable
          // message list as soon as loading flips true (e.g. right after
          // Confirm, before any new message has arrived), so it needs its
          // own scroll trigger rather than relying on the next messages$ emit.
          this.shouldScrollToBottom = true;
        } else {
          // Re-format all messages once streaming completes so patterns that
          // only match on complete lines (e.g. hotel rating expandables) render correctly.
          this.messages.forEach((msg, i) => {
            if (msg.role === 'assistant') {
              this.formattedTextMap[i] = this.formatMarkdown(msg.text);
            }
          });
          setTimeout(() => this.messageInput?.nativeElement?.focus(), 50);
        }
      })
    );

    // ENT-16320 — the TMC's curated FAQ list for the landing view.
    this.subscriptions.add(
      this.chatbotService.faqs$.subscribe((faqs) => {
        this.faqs = faqs;
      })
    );

    // Show typing indicator while a chatbot-triggered rail search is in flight.
    this.subscriptions.add(
      this.chatbotService.searchingState$.subscribe((state) => {
        this.isSearching = state.searching;
        this.searchingTravelType = state.travelType;
      })
    );

    // Subscribe to token streaming for real-time UI updates.
    // ENT-16247 — chat:token / chat:response now ride the same ChatbotService
    // socket (BaseChatbotService owns the listeners), so no second connection.
    this.subscriptions.add(
      this.chatbotService.tokenEvents$.subscribe((tokenEvent: ChatTokenEvent) => {
        this.handleIncomingToken(tokenEvent);
      })
    );

    // Reset the per-turn token buffer when the final chat:response lands.
    this.subscriptions.add(
      this.chatbotService.messages$.subscribe(() => {
        // messages$ emits on every chat:response (final), which is the cue to
        // start a fresh streaming buffer for the next turn.
        this.currentTokenBuffer = '';
      })
    );

    // ENT-13896 — voice input wiring.
    this.subscriptions.add(
      this.voiceRecognition.isListening().subscribe((listening) => {
        this.isListening = listening;
      })
    );
    this.subscriptions.add(
      this.voiceRecognition.transcripts().subscribe((transcript) => {
        // Match mobile behaviour: send the dictated text straight to the
        // assistant rather than dropping it into the input box for review.
        if (transcript && !this.isLoading) {
          this.chatbotService.sendMessage(transcript);
        }
      })
    );
    this.subscriptions.add(
      this.voiceRecognition.errors().subscribe((message) => {
        // Surface the failure inline in the input as a placeholder hint;
        // a full toast/snackbar would be overkill for this widget.
        console.warn('[ChatWidget] Voice recognition error:', message);
      })
    );
  }

  /**
   * Handle incoming token event and update the last assistant message in real-time.
   */
  private handleIncomingToken(tokenEvent: ChatTokenEvent): void {
    // Accumulate token
    this.currentTokenBuffer += tokenEvent.token;

    // Update the last assistant message if it exists
    if (this.messages.length > 0) {
      const lastMsg = this.messages[this.messages.length - 1];
      if (lastMsg.role === 'assistant') {
        // Update the raw text with accumulated buffer
        lastMsg.text = this.currentTokenBuffer;
        // Update formatted text for markdown rendering
        this.formattedTextMap[this.messages.length - 1] = this.formatMarkdown(
          this.currentTokenBuffer
        );

        // Trigger change detection and scroll
        this.shouldScrollToBottom = true;
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.chatbotService.cancelPendingRailTravelcards();
    this.subscriptions.unsubscribe();
    this.chatbotService.disconnect();
    this.voiceRecognition.destroy();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.hasUnreadPrompt = false;
      this.chatbotService.connect();
      this.chatbotService.requestFaqs();
      setTimeout(() => this.messageInput?.nativeElement?.focus(), 100);
    }
  }

  close(): void {
    // ENT-16311 - closing the chat is the traveller walking away from the
    // travelcard question. Leaving it parked holds the basket-add open, which
    // keeps the reason prompts suppressed for every add in the session.
    this.chatbotService.cancelPendingRailTravelcards();
    this.isOpen = false;
    this.tooltipVisible = false;
  }

  showTooltip(event: MouseEvent, text: string): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltipX = rect.left + rect.width / 2;
    this.tooltipY = rect.top - 8;
    this.tooltipText = text;
    this.tooltipVisible = true;
  }

  hideTooltip(): void {
    this.tooltipVisible = false;
  }

  onApproval(action: string): void {
    this.chatbotService.resume(action === 'APPROVE_BOOKING');
  }

  /** Continue button under the automatic rail seat-reservation message —
   *  sends the exact silent marker text `routeAfterInputGuardrails`/
   *  `bookingInfoRouter` in ai-agents recognise deterministically to resume
   *  the booking_info sequence (phoneNumber etc.) after the reservation. */
  onRailSeatReservationContinue(): void {
    void this.chatbotService.sendMessage(
      'Continue after rail seat reservation.',
      false,
      false,
      true
    );
  }

  bookingStage(message: ChatMessage): 'terms' | 'final' {
    const state = this.bookingAcknowledgements[message.id];
    return state?.final ? 'final' : 'terms';
  }

  bookingAcknowledgement(message: ChatMessage, key: 'terms' | 'identity'): boolean {
    return !!this.bookingAcknowledgements[message.id]?.[key];
  }

  bookingConfirmationInProgress(message: ChatMessage): boolean {
    return !!this.bookingAcknowledgements[message.id]?.confirming;
  }

  setBookingAcknowledgement(
    message: ChatMessage,
    key: 'terms' | 'identity',
    checked: boolean
  ): void {
    const current = this.bookingAcknowledgements[message.id] ?? {
      started: false,
      terms: false,
      identity: false,
      final: false
    };
    this.bookingAcknowledgements[message.id] = { ...current, [key]: checked };
  }

  continueBooking(message: ChatMessage): void {
    const current = this.bookingAcknowledgements[message.id] ?? {
      started: false,
      terms: false,
      identity: false,
      final: false
    };
    this.bookingAcknowledgements[message.id] = { ...current, started: true, final: true };
    this.scrollToBottom();
  }

  confirmBooking(message: ChatMessage): void {
    const current = this.bookingAcknowledgements[message.id] ?? {
      started: false,
      terms: false,
      identity: false,
      final: false,
      confirming: false
    };
    if (current.confirming) {
      return;
    }
    this.bookingAcknowledgements[message.id] = { ...current, confirming: true };
    message.bookingSummaryDismissed = true;
    void this.chatbotService.sendMessage('Yes, confirm and book', true);
  }

  declineBooking(message: ChatMessage): void {
    message.bookingSummaryDismissed = true;
    void this.chatbotService.sendMessage(
      'No, leave the products in my basket and keep all saved booking information.',
      false,
      true
    );
  }

  // ENT-16881 — immediate post-selection "book now or not yet" gate handlers.
  confirmBookNow(message: ChatMessage): void {
    if (!message.bookNowPrompt) {
      return;
    }
    this.chatbotService.confirmBookNow(message.bookNowPrompt.basketId);
  }

  declineBookNow(message: ChatMessage): void {
    if (!message.bookNowPrompt) {
      return;
    }
    this.chatbotService.declineBookNow(message.bookNowPrompt.basketId);
  }

  continueRailSeatReservation(message: ChatMessage): void {
    const reservationMessage = message as RailSeatReservationMessage;
    if (!reservationMessage.railSeatReservationPrompt || !message.id) {
      return;
    }
    if (this.railSeatReservationContinues.has(message.id)) {
      return;
    }
    this.railSeatReservationContinues.add(message.id);
    void this.chatbotService.sendMessage(
      'Continue after rail seat reservation.',
      false,
      false,
      true
    );
  }
  // ENT-18234 — second-leg selection for a return rail booking. The options render
  // inside the outbound card the fare was picked from, so the card answers its own
  // question and the user is not sent to a separate message to do it.
  /** ENT-18234 — is this message's outbound card currently asking for the second
   *  leg? Its inbound cards list the same return trains, so they come off screen. */
  railReturnQuestionLive(message: ChatMessage): boolean {
    return !!this.activeRailReturnPrompt && (message.id || '') === this.latestRailResultsMessageId;
  }

  railReturnPromptForCard(
    card: ChatRailResultCard,
    message: ChatMessage
  ): ChatRailReturnPrompt | null {
    const prompt = this.activeRailReturnPrompt;
    if (!prompt || (message.id || '') !== this.latestRailResultsMessageId) {
      return null;
    }
    return prompt.outboundJourneyIndex === card.index ? prompt : null;
  }

  // ENT-16431 — MI (cost centre) collection handlers.
  onMiAnswer(answer: ChatMiAnswer): void {
    this.chatbotService.resumeMi(answer, this.miAnswerEchoText(answer));
  }

  /** Human-readable form of a chosen MI answer for the echoed user bubble. */
  private miAnswerEchoText(answer: ChatMiAnswer): string | undefined {
    const chosen = answer.managementInfoValue;
    if (chosen?.value) {
      return chosen.label ? `${chosen.value} (${chosen.label})` : chosen.value;
    }
    const free = answer.freeValue?.trim();
    return free ? free : undefined;
  }

  onMiConfirm(): void {
    this.chatbotService.resumeMi(true);
  }

  /** ENT-16431 — user clicked the edit icon on a recap row; re-ask that field. */
  onMiFieldEdit(fieldId: string): void {
    this.chatbotService.resumeMiEditField(fieldId);
  }

  /** WIP — currently unused since the inline card is commented out in the
   * template (see `chat-widget.component.html`); kept ready for when the
   * card's UI is polished and both are re-enabled together. Forwards the
   * result (same shape the `FlightExtrasDialogComponent` modal used to
   * resolve with) back to whichever `getFlightDetailExtras` call is awaiting
   * it. */
  onFlightExtrasResolve(id: string, result: any): void {
    this.chatbotService.resolveFlightExtras(id, result);
  }

  /** ENT-16311 - settles the travelcard card, which is holding a rail
   *  add-to-basket open until the traveller answers it. */
  onRailTravelcardsResolve(id: string, result: RailTicketExtra[] | false): void {
    this.chatbotService.resolveRailTravelcards(id, result);
  }
  // Chatbot phone-number picker widget — renders for the first not-yet-completed passenger on
  // the last booking_info(field="phoneNumber") get result; the widget's
  // answer is sent back as an ordinary chat message for the model to
  // interpret (same mechanism as any typed reply), rather than a
  // deterministic resume — see shared.prompt.ts's phoneNumber handling.
  // NOTE: a passenger's `savedValue` here is just their previously-known
  // number (from this basket item or their profile) — its presence does NOT
  // mean the widget has already been answered.
  phoneNumberPassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  dateOfBirthPassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    return bookingInfo ? this.firstUnresolvedPassenger(bookingInfo) : null;
  }

  onDateOfBirthAnswer(passengerName: string, passengerId: string, dateOfBirth: string): void {
    this.markBookingInfoPassengerComplete('dateOfBirth', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), use date of birth ${dateOfBirth}.`,
      false,
      false,
      true
    );
  }

  onDateOfBirthSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('dateOfBirth', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the date of birth.`,
      false,
      false,
      true
    );
  }

  private firstUnresolvedPassenger(bookingInfo: BookingInfo): BookingInfoPassenger | null {
    const selected = this.bookingInfoPassengerSelections.get(bookingInfo);
    if (selected) {
      return selected;
    }

    const completed = this.completedBookingInfoPassengers[bookingInfo.field] ?? new Set<string>();
    const passenger =
      bookingInfo.passengers.find((candidate) => !completed.has(candidate.id)) ??
      bookingInfo.passengers[0] ??
      null;
    if (passenger) {
      this.bookingInfoPassengerSelections.set(bookingInfo, passenger);
    }
    return passenger;
  }

  private markBookingInfoPassengerComplete(field: string, passengerId: string): void {
    (this.completedBookingInfoPassengers[field] ??= new Set<string>()).add(passengerId);
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  phoneNumberQuestionText(passengerName: string): string {
    return `What's the best contact number for ${passengerName}?`;
  }

  railSeatLines(seat: string): string[] {
    const match = seat.match(/^Coach:\s*(.*?)\s+Number:\s*(.*?)\s+Notes:\s*(.*)$/);
    if (!match) {
      return [seat];
    }
    return [`Coach: ${match[1]}`, `Number: ${match[2]}`, `Notes: ${match[3]}`];
  }

  railSeatJourneySections(
    journeys: Array<{ direction?: string; route?: string; seats: string[] }>
  ): Array<{
    heading: string;
    journeys: Array<{ route?: string; seats: string[] }>;
  }> {
    const sections = new Map<string, Array<{ route?: string; seats: string[] }>>();
    for (const journey of journeys) {
      const key = journey.direction?.toLowerCase() === 'inward' ? 'inbound' : 'outbound';
      const group = sections.get(key) ?? [];
      group.push(journey);
      sections.set(key, group);
    }
    return ['outbound', 'inbound']
      .filter((direction) => sections.has(direction))
      .map((direction) => ({
        heading: direction === 'outbound' ? 'Outbound journey' : 'Inbound journey',
        journeys: sections.get(direction)!
      }));
  }

  onPhoneNumberAnswer(passengerName: string, passengerId: string, answer: ChatPhoneAnswer): void {
    this.markBookingInfoPassengerComplete('phoneNumber', passengerId);
    // Sent as a structured __chatAction (like setBookingAddress/
    // setAdditionalEmail) rather than a plain sentence — a plain-text digit
    // run this length can otherwise be misidentified as PII (e.g. a UK NHS
    // number) and redacted by Presidio before the model ever sees it.
    const text = JSON.stringify({
      __chatAction: 'setPhoneNumber',
      passengerId,
      countryCode: answer.countryCode,
      ...(answer.kind === 'saved' && answer.areaCode ? { areaCode: answer.areaCode } : {}),
      number: answer.number
    });
    // silent=true — the widget already shows the chosen number once locked;
    // no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onPhoneNumberSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('phoneNumber', passengerId);
    void this.chatbotService.sendMessage(
      JSON.stringify({ __chatAction: 'setPhoneNumber', passengerId, skip: true }),
      false,
      false,
      true
    );
  }

  // Chatbot passport picker widget — mirrors the phone-number widget exactly
  // (see shared.prompt.ts's passport handling): renders for the first
  // passenger on the last booking_info(field="passport") get result, and the
  // widget's answer is sent back as an ordinary chat message listing every
  // sub-part explicitly and separately so the model never has to infer or
  // recall a value itself.
  passportPassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  passportQuestionText(passengerName: string): string {
    return `Could you provide ${passengerName}'s passport details?`;
  }

  onPassportAnswer(passengerName: string, passengerId: string, answer: ChatPassportAnswer): void {
    this.markBookingInfoPassengerComplete('passport', passengerId);
    const savedPrefix =
      answer.kind === 'saved'
        ? `For passenger ${passengerId} (${passengerName}), use their saved "${answer.description}" passport: `
        : `For passenger ${passengerId} (${passengerName}), use this passport: `;
    const fields = [
      ['saved passport document ID', answer.documentId],
      ['passport number', answer.passportNumber],
      ['passport country', answer.passportCountry],
      ['nationality', answer.nationality],
      ['issuing state code', answer.codeOfIssuingState],
      ['date of birth', answer.dateOfBirth],
      ['date of issue', answer.dateOfIssue],
      ['expiry date', answer.dateOfExpiry],
      ['gender', answer.gender]
    ]
      .filter(([, value]) => value != null && value !== '')
      .map(([label, value]) => `${label} ${value}`)
      .join(', ');
    const text = `${savedPrefix}${fields}.`;
    // silent=true — the widget already shows the chosen passport once locked;
    // no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onPassportSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('passport', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the passport.`,
      false,
      false,
      true
    );
  }

  // Chatbot loyalty-programme picker widget — mirrors the phone-number/
  // passport widgets exactly (see shared.prompt.ts's loyaltyProgramme
  // handling): renders for the first passenger on the last
  // booking_info(field="loyaltyProgramme") get result — the airline
  // dropdown itself now looks airlines up live (see chat-loyalty-input's own
  // EnterpriseSearchService.getAirlinesList() call) rather than reading a
  // preloaded `choices` list from the get result.
  loyaltyProgrammePassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  loyaltyQuestionText(passengerName: string): string {
    return `Which loyalty programme would you like to add for ${passengerName}?`;
  }

  onLoyaltyAnswer(passengerName: string, passengerId: string, answer: ChatLoyaltyAnswer): void {
    this.markBookingInfoPassengerComplete('loyaltyProgramme', passengerId);
    const text =
      `For passenger ${passengerId} (${passengerName}), use ${answer.programmeTitle} (${answer.programme}) loyalty ` +
      `programme with membership number ${answer.number}.`;
    // silent=true — the widget already shows the chosen airline/number once
    // locked; no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onLoyaltySkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('loyaltyProgramme', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the loyalty programme.`,
      false,
      false,
      true
    );
  }

  // Chatbot meal-option picker widget — mirrors the phone-number/passport/
  // loyalty-programme widgets exactly (see shared.prompt.ts's mealOptions
  // handling): renders for the first passenger on the last
  // booking_info(field="mealOptions") get result, with the dropdown sourced
  // from the get result's top-level `choices` (clear-English titles mapped
  // to the underlying meal codes).
  mealOptionsPassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  // Meal choices come back from booking_info as `{ title, value }[]` on the
  // top-level `choices` field — cast defensively since BookingInfo types it
  // as `unknown`.
  mealChoices(bookingInfo: BookingInfo | undefined): ChatChoice[] {
    const choices = bookingInfo?.choices;
    return Array.isArray(choices)
      ? (choices as ChatChoice[]).filter((choice) => choice.value.toLowerCase() !== 'none')
      : [];
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  mealQuestionText(passengerName: string): string {
    return `Which meal option would you like for ${passengerName}?`;
  }

  onMealAnswer(passengerName: string, passengerId: string, answer: ChatChoiceAnswer): void {
    this.markBookingInfoPassengerComplete('mealOptions', passengerId);
    const text = `For passenger ${passengerId} (${passengerName}), use the ${answer.title} meal option.`;
    // silent=true — the widget already shows the chosen meal option once
    // locked; no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onMealSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('mealOptions', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the meal option.`,
      false,
      false,
      true
    );
  }

  // Same rendering/wiring pattern as the meal-option widget above (locked-instance
  // handling): renders for the first passenger on the last
  // booking_info(field="specialServiceRequest") get result, with the dropdown
  // sourced from the get result's top-level `choices` (clear-English titles
  // mapped to the underlying special-service-request codes).
  specialServiceRequestPassenger(
    bookingInfo: BookingInfo | undefined
  ): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  // Special-service-request choices come back from booking_info as
  // `{ title, value }[]` on the top-level `choices` field — cast defensively
  // since BookingInfo types it as `unknown`.
  specialServiceRequestChoices(bookingInfo: BookingInfo | undefined): ChatChoice[] {
    const choices = bookingInfo?.choices;
    return Array.isArray(choices)
      ? (choices as ChatChoice[]).filter((choice) => choice.value.toLowerCase() !== 'none')
      : [];
  }

  bookingInfoChoices(bookingInfo: BookingInfo | undefined): ChatChoice[] {
    const choices = bookingInfo?.choices;
    return Array.isArray(choices) ? (choices as ChatChoice[]) : [];
  }

  accountSavedValue(bookingInfo: BookingInfo | undefined): Record<string, unknown> | null {
    const savedValue = bookingInfo?.savedValue;
    return typeof savedValue === 'string'
      ? { account: savedValue }
      : ((savedValue as Record<string, unknown> | null) ?? null);
  }

  onAccountAnswer(answer: ChatChoiceAnswer): void {
    const text = `Use account ${answer.value} (${answer.title}) for this rail booking.`;
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  ticketQueueSavedValue(bookingInfo: BookingInfo | undefined): Record<string, unknown> | null {
    const savedValue = bookingInfo?.savedValue;
    return typeof savedValue === 'string'
      ? { ticketQueue: savedValue }
      : ((savedValue as Record<string, unknown> | null) ?? null);
  }

  onTicketQueueAnswer(answer: ChatChoiceAnswer): void {
    const text = JSON.stringify({
      __chatAction: 'setTicketQueue',
      ticketQueue: answer.value,
      ...(answer.email ? { ticketQueueEmail: answer.email } : {}),
      ...(answer.stationCode ? { ticketQueueCollectionStation: answer.stationCode } : {})
    });
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  ticketDeliveryOptionSavedValue(
    bookingInfo: BookingInfo | undefined
  ): Record<string, unknown> | null {
    const savedValue = bookingInfo?.savedValue;
    return typeof savedValue === 'string'
      ? { ticketDeliveryOption: savedValue }
      : ((savedValue as Record<string, unknown> | null) ?? null);
  }

  isActiveTicketDeliveryOption(message: ChatMessage, index: number): boolean {
    return (
      message.bookingInfo?.field === 'ticketDeliveryOption' &&
      message.bookingInfo.applicable === true &&
      !this.messages
        .slice(index + 1)
        .some(
          (candidate) =>
            candidate.bookingInfo?.field === 'ticketDeliveryOption' &&
            candidate.bookingInfo.applicable === true
        )
    );
  }

  onTicketDeliveryOptionSelection(): void {
    this.scrollToBottom();
  }

  onTicketDeliveryOptionAnswer(answer: ChatChoiceAnswer): void {
    const text = JSON.stringify({
      __chatAction: 'setTicketDeliveryOption',
      ticketDeliveryOption: answer.value,
      ...(answer.email ? { ticketDeliveryOptionEmail: answer.email } : {})
    });
    this.scrollToBottom();
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  specialServiceRequestQuestionText(passengerName: string): string {
    return `Do you need a special service request for ${passengerName}?`;
  }

  onSpecialServiceRequestAnswer(
    passengerName: string,
    passengerId: string,
    answer: ChatChoiceAnswer
  ): void {
    this.markBookingInfoPassengerComplete('specialServiceRequest', passengerId);
    const text = `For passenger ${passengerId} (${passengerName}), use the ${answer.title} special service request.`;
    // silent=true — the widget already shows the chosen special service request
    // once locked; no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onSpecialServiceRequestSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('specialServiceRequest', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the special service request.`,
      false,
      false,
      true
    );
  }

  // Same rendering/wiring pattern as the widgets above (locked-instance
  // handling): renders for the first passenger on the last
  // booking_info(field="additionalEmail") get result. Unlike the other
  // widgets this field has no `choices` — the widget is just up to 3 free-text
  // email fields.
  additionalEmailPassenger(bookingInfo: BookingInfo | undefined): BookingInfoPassenger | null {
    if (!bookingInfo) {
      return null;
    }
    return this.firstUnresolvedPassenger(bookingInfo);
  }

  // Question text shown inside the chat bubble itself (the widget below no
  // longer renders its own duplicate label — see showLabel on both widgets).
  additionalEmailQuestionText(passengerName: string): string {
    return `Would you like to add any additional email addresses for ${passengerName}?`;
  }

  addressQuestionText(field: 'bookingAddress' | 'deliveryAddress' | 'collectionAddress'): string {
    if (field === 'deliveryAddress') {
      return 'Where would you like the car delivered?';
    }
    if (field === 'collectionAddress') {
      return 'Where would you like the car collected?';
    }
    return 'Please select a booking address, or choose None to enter a different address.';
  }

  isSameAddressQuestion(message: ChatMessage): boolean {
    return (
      message.role === 'assistant' &&
      message.text?.trim() === 'Should the collection address be the same as the delivery address?'
    );
  }

  chooseSameAddress(message: ChatMessage, sameLocation: boolean): void {
    if (!message.id || this.sameAddressChoices.has(message.id)) return;
    this.sameAddressChoices.add(message.id);
    void this.chatbotService.sendMessage(
      sameLocation
        ? 'The collection address is the same as the delivery address.'
        : 'The collection address is different from the delivery address.',
      false,
      false,
      true
    );
  }

  flightDetailsQuestionText(): string {
    return 'What are the airline carrier and flight number for your airport pickup?';
  }

  onFlightDetailsAnswer(answer: ChatFlightDetailsAnswer): void {
    void this.chatbotService.sendMessage(
      `For the airport car pickup, set the item-level flight details: flightCarrier=${answer.flightCarrier}, flightNumber=${answer.flightNumber}. Do not ask for these details again; continue to the next booking-information step.`,
      false,
      false,
      true
    );
  }

  onCarDetailsAnswer(answer: ChatCarDetailsAnswer): void {
    void this.chatbotService.sendMessage(
      `For the airport car parking, set the item-level car details: carColour=${answer.carColour}, carMake=${answer.carMake}, carModel=${answer.carModel}, registration=${answer.registration}. Do not ask for these details again; continue to the next booking-information step.`,
      false,
      false,
      true
    );
  }

  onJourneyDetailsSkip(): void {
    void this.chatbotService.sendMessage(
      'For the airport car parking, skip the journey details.',
      false,
      false,
      true
    );
  }

  onJourneyDetailsAnswer(answer: ChatJourneyDetailsAnswer): void {
    void this.chatbotService.sendMessage(
      `For the airport car parking, set the item-level journey details: destination=${answer.destination}, returnTerminal=${answer.returnTerminal}, returnFlight=${answer.returnFlight}, outTerminal=${answer.outTerminal} outFlight=${answer.outFlight} mobileNum=${answer.mobileNum}. Do not ask for these details again; continue to the next booking-information step.`,
      false,
      false,
      true
    );
  }

  onCarDetailsSkip(): void {
    void this.chatbotService.sendMessage(
      'For the airport car parking, skip the car details.',
      false,
      false,
      true
    );
  }

  onFlightDetailsSkip(): void {
    void this.chatbotService.sendMessage(
      'For the airport car pickup, skip the flight details.',
      false,
      false,
      true
    );
  }

  onAddressAnswer(
    field: 'bookingAddress' | 'deliveryAddress' | 'collectionAddress',
    answer: ChatAddressAnswer
  ): void {
    const address = answer.address;
    const text = JSON.stringify({
      __chatAction: 'setBookingAddress',
      field,
      address
    });
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  driverNotesQuestionText(): string {
    return 'Do you want to add any driver notes?';
  }

  onDriverNotesAnswer(answer: ChatDriverNotesAnswer): void {
    void this.chatbotService.sendMessage(
      `Set the item-level driver notes: ${answer.notes}. Do not ask for this again; continue to the next booking-information step.`,
      false,
      false,
      true
    );
  }

  onDriverNotesSkip(): void {
    void this.chatbotService.sendMessage('Skip the driver notes.', false, false, true);
  }

  flightNumberQuestionText(): string {
    return 'Do you have a flight number to add?';
  }

  onFlightNumberAnswer(answer: ChatFlightNumberAnswer): void {
    void this.chatbotService.sendMessage(
      `Set the item-level flight number: ${answer.flightNumber}. Do not ask for this again; continue to the next booking-information step.`,
      false,
      false,
      true
    );
  }

  onFlightNumberSkip(): void {
    void this.chatbotService.sendMessage('Skip the flight number.', false, false, true);
  }

  onAdditionalEmailAnswer(
    passengerName: string,
    passengerId: string,
    answer: ChatEmailAnswer
  ): void {
    this.markBookingInfoPassengerComplete('additionalEmail', passengerId);
    // Sent as a structured __chatAction (like setBookingAddress/
    // selectPaymentMethod) rather than a plain sentence
    const text = JSON.stringify({
      __chatAction: 'setAdditionalEmail',
      passengerId,
      emails: answer.emails
    });
    // silent=true — the widget already shows the entered address(es) once
    // locked; no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onAdditionalEmailSkip(passengerName: string, passengerId: string): void {
    this.markBookingInfoPassengerComplete('additionalEmail', passengerId);
    void this.chatbotService.sendMessage(
      `For passenger ${passengerId} (${passengerName}), skip the additional email.`,
      false,
      false,
      true
    );
  }

  onCvvAnswer(cvv: string): void {
    const cleanCvv = cvv?.trim();
    if (!cleanCvv || !/^\d{3,4}$/.test(cleanCvv)) {
      alert('Please enter a valid 3-4 digit CVV');
      return;
    }
    this.markBookingInfoPassengerComplete('cvv', 'cvv');
    const text = JSON.stringify({
      __chatAction: 'setCvv',
      cvv: cleanCvv
    });
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  onCvvSkip(): void {
    this.markBookingInfoPassengerComplete('cvv', 'cvv');
    void this.chatbotService.sendMessage('Skip CVV entry', false, false, true);
  }

  // Chatbot payment-method picker widget — shown only when the payment_method
  // tool's action="get" call found 2+ options with none selected (a single
  // option is auto-selected by the model itself, no widget needed). Unlike
  // the booking_info field widgets above this isn't per-passenger and has no
  // Skip — a payment method is always mandatory before booking.
  paymentMethodQuestionText(prompt?: PaymentMethodPrompt): string {
    if (prompt?.productName) {
      const productName = prompt.productName;
      return $localize`:@@chatPaymentMethodQuestionProduct:Which payment method would you like to use for your ${productName}:productName:?`;
    }
    return $localize`:@@chatPaymentMethodQuestion:Which payment method would you like to use?`;
  }

  paymentMethodChoices(prompt: PaymentMethodPrompt | undefined): ChatPaymentMethodChoice[] {
    return prompt?.options ?? [];
  }

  onPaymentMethodAnswer(answer: ChatPaymentMethodAnswer, prompt?: PaymentMethodPrompt): void {
    if (answer.id === 'apexx_add_new' || answer.type === 'apexx_new_card') {
      const label = answer.cardLabel?.trim();
      if (!label) {
        return;
      }
      const sub = this.userService.updateApexxCard.subscribe((updated: boolean) => {
        if (updated) {
          sub.unsubscribe();
          const refreshMsg = `I have added a new card${prompt?.productName ? ` for my ${prompt.productName}` : ''}. Please refresh the payment options.`;
          void this.chatbotService.sendMessage(refreshMsg, false, false, false);
        }
      });
      this.userService.createCardApexx(label).subscribe({
        next: (cards) => {
          if (cards.apexxURL) {
            this.userService.showApexxDialog(cards.apexxURL, '', 400, 400);
          }
        },
        error: (error) => {
          sub.unsubscribe();
          if (error?.message === 'A token exists with same label on user level.') {
            alert('Incorrect card name. There is a card with the same name');
          }
        }
      });
      return;
    }

    const payload: Record<string, unknown> = {
      __chatAction: 'selectPaymentMethod',
      paymentMethodId: answer.id,
      basketItemId: answer.basketItemId
    };
    if (answer.apexxTokenId) {
      payload['apexxTokenId'] = answer.apexxTokenId;
    }
    const text = JSON.stringify(payload);
    // silent=true — the widget already shows the chosen payment method once
    // locked; no need to echo this instruction text as a user chat bubble too.
    void this.chatbotService.sendMessage(text, false, false, true);
  }

  // Chatbot approver picker widget — shown only for the Manual Select
  // Approver / Multi-Level Manual Approval configs, one outstanding
  // rule/level at a time (see select_approver's action="get"/"select"
  // nextApprovalStep). Pre-selected Approver and Profile Approver need no
  // user interaction at all and never surface this widget. Like
  // payment_method this isn't per-passenger and has no Skip — an outstanding
  // approval level is always mandatory before booking.
  approverSelectionQuestionText(prompt?: ApproverSelectionPrompt): string {
    // Multi-Level Manual Approval — call out that this is a sequential
    // multi-level flow (mirrors the old approvers-modal's per-level labels),
    // so the user isn't surprised by a second approver prompt after the first.
    if (prompt && prompt.totalLevels > 1) {
      const level = prompt.level;
      const totalLevels = prompt.totalLevels;
      return $localize`:@@chatApproverSelectPromptLevel:Please select approver for level ${level}:level: of ${totalLevels}:totalLevels:`;
    }
    return $localize`:@@chatApproverSelectPrompt:Please select approver`;
  }

  approverSelectionChoices(prompt: ApproverSelectionPrompt | undefined): ChatApproverChoice[] {
    return prompt?.availableApprovers ?? [];
  }

  onApproverSelectionAnswer(answer: ChatApproverAnswer): void {
    // ruleId, level, and approverId are labelled explicitly so the model
    // never has to infer or recall them itself when it calls select_approver
    // action="select" — same reasoning as the phone-number widget's country
    // code/number split above.
    const text =
      `Select approver ${answer.approverName} (approver id ${answer.approverId}) for rule ${answer.ruleId}, ` +
      `level ${answer.level}.`;
    void this.chatbotService.sendMessage(text, true, false, true);
  }

  startNewConversation(): void {
    this.chatbotService.clearSession();
    this.messages = [];
    this.completedBookingInfoPassengers = {};
    this.bookingInfoPassengerSelections = new WeakMap<BookingInfo, BookingInfoPassenger>();
    this.formattedTextMap = {};
    this.bookingSelectionFlags = {};
    this.feedbackRatings = {};
    this.feedbackReasons = {};
    this.feedbackComments = {};
    this.feedbackConfirmed = {};
    this.feedbackDetailOpenFor = null;
    this.activeFeedbackReasons = [];
    this.currentTokenBuffer = '';
    this.resetFaqView();
  }

  // ── ENT-16320 — FAQ browse-and-select ────────────────────────────────────

  /** FAQs shown on the landing view: first N, or all once expanded. */
  get visibleFaqs(): BotFaq[] {
    if (this.faqExpanded || this.faqs.length <= this.faqPreviewCount) {
      return this.faqs;
    }
    return this.faqs.slice(0, this.faqPreviewCount);
  }

  get hiddenFaqCount(): number {
    return Math.max(0, this.faqs.length - this.faqPreviewCount);
  }

  openFaq(faq: BotFaq): void {
    this.selectedFaq = faq;
    this.selectedFaqAnswerHtml = this.formatMarkdown(faq.answer);
  }

  closeFaq(): void {
    this.selectedFaq = null;
    this.selectedFaqAnswerHtml = '';
  }

  toggleFaqExpanded(): void {
    this.faqExpanded = !this.faqExpanded;
  }

  /** Collapse the detail view and reset the list to its preview state. */
  private resetFaqView(): void {
    this.selectedFaq = null;
    this.selectedFaqAnswerHtml = '';
    this.faqExpanded = false;
  }

  /**
   * ENT-16247 — thumbs up/down handler. Optimistically store the rating
   * and open the feedback detail panel. Submission occurs when "Submit" or "Skip" is clicked.
   */
  rateMessage(msg: ChatMessage, rating: 'up' | 'down'): void {
    if (!msg?.id || msg.role !== 'assistant') {
      return;
    }
    if (this.feedbackRatings[msg.id] === rating) {
      // Toggle the detail panel closed if the same rating is clicked twice.
      const reopen = this.feedbackDetailOpenFor !== msg.id;
      this.feedbackDetailOpenFor = reopen ? msg.id : null;
      this.activeFeedbackReasons = reopen ? this.reasonsForRating(rating) : [];
      if (reopen) {
        this.scrollFeedbackIntoView();
      }
      return;
    }
    const previous = this.feedbackRatings[msg.id];
    // Switching rating clears any previously selected reason chip.
    if (previous && previous !== rating) {
      delete this.feedbackReasons[msg.id];
    }

    // Store rating locally and open panel optimistically
    this.feedbackRatings[msg.id] = rating;
    this.feedbackDetailOpenFor = msg.id;
    this.activeFeedbackReasons = this.reasonsForRating(rating);
    // Reset confirmation if user re-rates.
    this.feedbackConfirmed[msg.id] = false;
    this.scrollFeedbackIntoView();
  }

  private reasonsForRating(rating: 'up' | 'down'): readonly string[] {
    // Canonical reason set shared with mobile via obtservices.
    return feedbackReasonsForRating(rating);
  }

  /** Toggle a reason chip. Submission occurs later on "Submit". */
  selectFeedbackReason(msg: ChatMessage, reason: string): void {
    if (!msg.id) return;
    this.feedbackReasons[msg.id] = this.feedbackReasons[msg.id] === reason ? '' : reason;
  }

  /** Send the current comment + reason + transcript snapshot, then confirm. */
  submitFeedbackDetail(msg: ChatMessage): void {
    if (!msg.id) return;
    this.sendFeedback(msg);
    this.feedbackDetailOpenFor = null;
    this.activeFeedbackReasons = [];
    this.showFeedbackConfirmation(msg.id);
  }

  /** Close the panel without sending extra detail; rating already counts. */
  skipFeedbackDetail(msg: ChatMessage): void {
    if (!msg.id) return;

    // Clear any drafted text or chips they selected before pressing skip
    delete this.feedbackReasons[msg.id];
    delete this.feedbackComments[msg.id];

    this.sendFeedback(msg);

    this.feedbackDetailOpenFor = null;
    this.activeFeedbackReasons = [];
    this.showFeedbackConfirmation(msg.id);
  }

  /**
   * Build the full feedback payload (rating + reason + comment + transcript)
   * and emit it. Returns whether the socket accepted the emit.
   */
  private sendFeedback(msg: ChatMessage): boolean {
    if (!msg.id) return false;
    const rating = this.feedbackRatings[msg.id];
    if (!rating) return false;

    // BaseChatbotService caches `sessionId` from every chat:token /
    // chat:response, so it owns session resolution entirely.
    const idx = this.messages.findIndex((m) => m.id === msg.id);
    const userQuestion = this.findPriorUserQuestion(idx);
    const transcript = this.snapshotTranscript(idx);

    return this.chatbotService.submitFeedback(msg.id, rating, {
      comment: this.feedbackComments[msg.id]?.trim() || undefined,
      reason: this.feedbackReasons[msg.id] || undefined,
      source: 'OBT',
      userQuestion,
      assistantResponse: msg.text,
      transcript
    });
  }

  private findPriorUserQuestion(assistantIndex: number): string | undefined {
    if (assistantIndex <= 0) return undefined;
    for (let i = assistantIndex - 1; i >= 0; i -= 1) {
      const m = this.messages[i];
      if (m.role === 'user' && m.text) return m.text;
    }
    return undefined;
  }

  private snapshotTranscript(uptoIndex: number): Array<{
    id?: string;
    role: 'user' | 'assistant';
    text: string;
    timestamp?: string;
  }> {
    const end = uptoIndex >= 0 ? uptoIndex + 1 : this.messages.length;
    return this.messages.slice(0, end).map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text,
      timestamp:
        (m as { timestamp?: Date }).timestamp instanceof Date
          ? ((m as { timestamp?: Date }).timestamp as Date).toISOString()
          : undefined
    }));
  }

  private showFeedbackConfirmation(messageId: string): void {
    this.feedbackConfirmed[messageId] = true;
    setTimeout(() => {
      this.feedbackConfirmed[messageId] = false;
    }, 3000);
  }

  async sendMessage(): Promise<void> {
    const text = this.inputText.trim();
    if (!text || this.isLoading) {
      return;
    }

    this.inputText = '';
    // Asking a question dismisses the FAQ landing view (ENT-16320).
    this.closeFaq();
    await this.chatbotService.sendMessage(text);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Toggle the browser's speech recogniser. The first start triggers the
   * native mic permission prompt; subsequent toggles just start/stop it.
   * The transcript flows through `VoiceRecognitionService.transcripts()`
   * and is auto-sent in `ngOnInit`.
   */
  async toggleVoice(): Promise<void> {
    if (!this.isVoiceSupported || this.isLoading) {
      return;
    }
    if (this.isListening) {
      this.voiceRecognition.stop();
      return;
    }
    // ENT-13896 — move focus into the input box on the same click that starts
    // the mic. The Web Speech recogniser only captures dictation while the
    // input is focused, so without this the user had to click the mic and then
    // click the input box (two clicks) before speech was accepted.
    this.messageInput?.nativeElement?.focus();
    try {
      await this.voiceRecognition.start();
      // Re-assert focus after the permission prompt resolves — granting mic
      // access can move focus to the browser chrome on the first use.
      this.messageInput?.nativeElement?.focus();
    } catch {
      // Error already surfaced via errors$ subscription.
    }
  }

  get searchingLabel(): string {
    const travelType =
      this.searchingTravelType ||
      [...this.messages].reverse().find((m) => m.parameters?.travelType)?.parameters?.travelType;
    switch (travelType) {
      case 'hotel':
        return 'Searching for hotels…';
      case 'car-hire':
        return 'Searching for cars…';
      case 'flight':
        return 'Searching for flights…';
      case 'rail':
        return 'Searching for trains…';
      case 'eurostar':
        return 'Searching for Eurostar journeys…';
      case 'taxi':
        return 'Searching for taxis…';
      case 'lounge':
        return 'Searching for lounges…';
      case 'fast-track':
        return 'Searching for fast track…';
      default:
        return 'Searching…';
    }
  }

  handleSuggestionButtonClick(
    action: 'FILL_FORM' | 'FILL_AND_SEARCH' | 'SELECT_RESULT' | 'REDIRECT_TO_MY_BOOKINGS',
    parameters: ChatSearchParameters | null | undefined,
    redirectParams?: ChatSuggestion['params']
  ): void {
    switch (action) {
      case 'FILL_AND_SEARCH':
        if (parameters) {
          this.onFillAndSearch(parameters);
        }
        break;
      case 'FILL_FORM':
      case 'SELECT_RESULT':
        if (parameters) {
          this.onFillForm(parameters);
        }
        break;
      case 'REDIRECT_TO_MY_BOOKINGS':
        this.redirectToMyBookings(redirectParams);
        break;
    }
  }

  /**
   * Hand the chatbot's upcoming-trips filters off to the My Bookings screen.
   * `fromDate` is mapped onto `travelDateFrom` so My Bookings reproduces the
   * same travel-date window the chatbot displayed.
   */
  private redirectToMyBookings(params?: ChatSuggestion['params']): void {
    const baseParams: MyBookingsBaseParams = {
      statuses: params?.statuses,
      travelDateFrom: params?.travelDateFrom ?? params?.fromDate,
      travelDateTo: params?.travelDateTo,
      traveller: params?.traveller,
      serviceTypes: params?.serviceTypes
    };
    this.chatbotService.setMyBookingsBaseParams(baseParams);
    if (this.router.url.split('?')[0] === '/my-bookings') {
      // Already on My Bookings: same-URL navigation is a no-op, so the search
      // component never re-reads the base params. Bounce through a throwaway
      // URL to force it to re-initialise and apply the chatbot's filters.
      this.router
        .navigateByUrl('/', { skipLocationChange: true })
        .then(() => this.router.navigate(['/my-bookings']));
    } else {
      this.router.navigate(['/my-bookings']);
    }
  }

  /** "View My Bookings" affordance on an upcoming-trips reply. */
  onViewMyBookings(msg: ChatMessage): void {
    const redirect = msg.suggestions?.find((s) => s.action === 'REDIRECT_TO_MY_BOOKINGS');
    this.redirectToMyBookings(redirect?.params);
  }

  /** "Show more" affordance — asks the assistant to list all upcoming bookings. */
  onShowMoreBookings(): void {
    if (this.isLoading) {
      return;
    }
    this.chatbotService.sendMessage('Show all my upcoming bookings');
  }

  onFillForm(parameters: ChatSearchParameters): void {
    this.chatbotService.fillForm(parameters);
  }

  onFillAndSearch(parameters: ChatSearchParameters): void {
    if (parameters.travelType === 'hotel') {
      this.router.navigate([], {
        queryParams: { hotelNameFilter: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    this.chatbotService.fillAndSearch(parameters);
  }

  onViewHotelResults(): void {
    const lastParams = this.chatbotHotelSearchService.lastParameters;
    if (lastParams && !this.chatbotHotelSearchService.hasHotelResults) {
      this.chatbotHotelSearchService.markAsLoading();
      this.chatbotHotelSearchService.handle({ ...lastParams }, true);
    }
    if (lastParams?.hotelFilters) {
      this.chatbotHotelSearchService.hotelSearchParams.pendingChatbotFilters = true;
    }
    this.router.navigate(['/results/hotels'], { queryParams: {} });
  }

  onHotelNameClick(hotelName: string): void {
    const lastParams = this.chatbotHotelSearchService.lastParameters;
    if (lastParams && !this.chatbotHotelSearchService.hasHotelResults) {
      this.chatbotHotelSearchService.handle({ ...lastParams }, true);
    }
    if (lastParams?.hotelFilters) {
      this.chatbotHotelSearchService.hotelSearchParams.pendingChatbotFilters = true;
    }
    this.router.navigate(['/results/hotels'], { queryParams: { hotelNameFilter: hotelName } });
  }

  onViewCarHireResults(): void {
    this.router.navigate(['/results/cars']);
  }

  onCarHireCardSelect(card: ChatCarHireResultCard): void {
    this.resultAwareService.selectCarHire(card.index - 1).catch((error) => {
      console.error('[ChatWidget] Car hire selection failed:', error);
    });
  }

  onDepotSelect(index: number): void {
    this.chatbotService.depotSelectionHandler?.(`${index + 1}`);
  }

  onTaxiViewFullResults(): void {
    const lastParams = this.chatbotTaxiSearchService.lastParameters;
    if (lastParams && !this.chatbotTaxiSearchService.hasTaxiResults) {
      this.chatbotTaxiSearchService.markAsLoading();
      this.chatbotTaxiSearchService.handle({ ...lastParams }, true);
    }
    this.chatbotTaxiSearchService.chatbotFilterId = null;
    this.router.navigate(['/results/taxis']);
  }

  onTaxiCardSelect(card: ChatTaxiResultCard): void {
    this.resultAwareService.selectTaxi(card.index - 1).catch((error) => {
      console.error('[ChatWidget] Taxi selection failed:', error);
    });
  }

  onLoungeCardSelect(card: ChatLoungeResultCard): void {
    this.resultAwareService.selectLounge(card.index).catch((error) => {
      console.error('[ChatWidget] Lounge selection failed:', error);
    });
  }

  onParkingCardSelect(card: ChatParkingResultCard): void {
    this.resultAwareService.selectParking(card.index).catch((error) => {
      console.error('[ChatWidget] Parking selection failed:', error);
    });
  }

  onFastTrackCardSelect(card: ChatFastTrackResultCard): void {
    this.resultAwareService.selectFastTrack(card.index).catch((error) => {
      console.error('[ChatWidget] Fast track selection failed:', error);
    });
  }

  onEurostarFareSelect(
    selection: ChatEurostarFareSelection,
    direction: 'outbound' | 'inbound'
  ): void {
    this.resultAwareService
      .selectEurostar(selection.cardIndex, selection.classType, direction)
      .catch((error) => {
        console.error('[ChatWidget] Eurostar selection failed:', error);
      });
  }

  onViewFullResults(): void {
    this.router.navigate(['/results/rail']);
  }

  onFlightViewFullResults(): void {
    // Same idea as rail: results live in the flight search service already,
    // we just need to land the user on the results page.
    this.router.navigate(['/results/flights']);
  }

  /** Deep-link to the picked flight on the results page, mirroring the hotel
   *  card's "click hotel → filtered results" flow. The flight results page reads
   *  `flightNumberFilter` (positional "outbound,inbound" tokens) and narrows the
   *  list to itineraries containing those segments. */
  onFlightFareSelect(selection: ChatFlightFareSelection): void {
    const { outboundFlightNumber, inboundFlightNumber } = selection;
    const flightNumberFilter = inboundFlightNumber
      ? `${outboundFlightNumber},${inboundFlightNumber}`
      : outboundFlightNumber;
    this.router.navigate(['/results/flights'], { queryParams: { flightNumberFilter } });
  }

  onEurostarViewFullResults(): void {
    this.router.navigate(['/results/eurostar']);
  }

  onLoungeViewFullResults(): void {
    this.router.navigate(['/results/lounges']);
  }
  onFastTrackViewFullResults(): void {
    this.router.navigate(['/results/fast-track']);
  }
  onParkingViewFullResults(): void {
    this.router.navigate(['/results/parkings']);
  }

  onCancelAction(action: string): void {
    // In-flight guard: a fast double-click (or a socket re-emit) could send the
    // same answer twice, producing a second backend turn that surfaces the
    // already-seeded next-journey prompt as a bare, button-less duplicate.
    // Ignore the click while a turn is still streaming.
    if (this.isLoading) {
      return;
    }

    const messages: Record<string, string> = {
      KEEP_RAIL_JOURNEY: 'Yes, this rail journey is still required.',
      CANCEL_RAIL_JOURNEY: 'No, cancel my booking.',
      CANCEL_RAIL_OUTBOUND: 'No, cancel outbound only.',
      CANCEL_RAIL_INBOUND: 'No, cancel return only.'
    };

    const text = messages[action];
    if (!text) return;

    // Send the fixed English text as the backend query (keeps LLM routing
    // language-independent), but show the localized label in the chat bubble.
    const displayText = this.cancelActionLabels[action];

    this.chatbotService.clearCancelSuggestions();
    this.chatbotService.sendMessage(text, false, false, false, displayText);
  }

  async onSelectAlternative(alternativeIndex: number): Promise<void> {
    try {
      await this.resultAwareService.executeSelection(alternativeIndex);
    } catch (error) {
      console.error('[ChatWidget] Alternative selection failed:', error);
    }
  }

  /** ENT-16881 — user clicked a room's rate price in a chat hotel card: add
   *  that room to the basket and start the booking journey (results-page
   *  `card.index` is 1-based, `selectHotelRoom` expects a 0-based array index). */
  async onHotelRoomSelect(selection: ChatHotelRoomSelection): Promise<void> {
    try {
      await this.resultAwareService.selectHotelRoom(selection.hotelIndex - 1, selection.roomId);
    } catch (error) {
      console.error('[ChatWidget] Hotel room selection failed:', error);
    }
  }

  private buildRailSummary(railResults: ChatRailResults): RailResultsSummary {
    const summaryDate = this.formatDate(railResults.parameters.departureDate);

    const outboundPrices = railResults.outbound
      .map((c) => c.fares?.singlePrice ?? c.fromPrice)
      .filter((p): p is number => p != null && p > 0);
    let cheapestSinglePrice: number | null = null;
    if (outboundPrices.length) {
      const cheapestOutbound = Math.min(...outboundPrices);
      if (railResults.isReturn) {
        const inboundPrices = (railResults.inbound ?? [])
          .map((c) => c.fares?.singlePrice ?? c.fromPrice)
          .filter((p): p is number => p != null && p > 0);
        if (inboundPrices.length) {
          cheapestSinglePrice = cheapestOutbound + Math.min(...inboundPrices);
        }
      } else {
        cheapestSinglePrice = cheapestOutbound;
      }
    }

    // Exclude dualSingleJourneyFares — those are two singles, not a return ticket.
    const returnPrices = railResults.outbound
      .flatMap((c) =>
        (c.fares?.returnTickets ?? [])
          .filter((t) => t.section !== 'dualSingleJourneyFares')
          .map((t) => t.price)
      )
      .filter((p): p is number => p != null && p > 0);
    const cheapestReturnPrice = returnPrices.length ? Math.min(...returnPrices) : null;

    return { summaryDate, cheapestSinglePrice, cheapestReturnPrice };
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    const day = date.getDate();
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    return `${weekday} ${day}${this.getOrdinal(day)} ${month}`;
  }

  private getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  // ENT-16993 — render entry for the message list. Never returns unescaped
  // text: formatted assistant markdown, else escaped raw text (e.g. user input).
  messageHtml(index: number, msg: ChatMessage): string {
    return this.formattedTextMap[index] || this.escapeHtml(msg?.text ?? '');
  }

  private formatMarkdown(text: string): string {
    if (!text) {
      return text;
    }

    // ENT-16993 — escape before parsing so tags in the text render as
    // characters; the converter below adds its own trusted tags on top.
    text = this.escapeHtml(text);

    const lines = text.split('\n');
    const html: string[] = [];
    let inList = false;
    let inOrderedList = false;

    for (const line of lines) {
      const trimmed = line.trim();

      // Headings: ### → h4, ## → h3 (keep small for chat bubbles)
      const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
      if (headingMatch) {
        if (inList) {
          html.push('</ul>');
          inList = false;
        }
        if (inOrderedList) {
          html.push('</ol>');
          inOrderedList = false;
        }
        const level = Math.min(headingMatch[1].length + 2, 6);
        html.push(`<h${level}>${this.inlineFormat(headingMatch[2])}</h${level}>`);
        continue;
      }

      // Ordered list items: 1. text
      const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (orderedMatch) {
        if (inList) {
          html.push('</ul>');
          inList = false;
        }
        if (!inOrderedList) {
          html.push('<ol>');
          inOrderedList = true;
        }
        html.push(`<li>${this.inlineFormat(orderedMatch[1])}</li>`);
        continue;
      }

      // Unordered list items: - text or * text
      const listMatch = trimmed.match(/^[-*]\s+(.+)$/);
      if (listMatch) {
        if (inOrderedList) {
          html.push('</ol>');
          inOrderedList = false;
        }
        if (!inList) {
          html.push('<ul>');
          inList = true;
        }
        html.push(`<li>${this.inlineFormat(listMatch[1])}</li>`);
        continue;
      }

      // Close any open list if we hit a non-list line
      if (inList) {
        html.push('</ul>');
        inList = false;
      }
      if (inOrderedList) {
        html.push('</ol>');
        inOrderedList = false;
      }

      // Empty line → spacing
      if (!trimmed) {
        html.push('<br>');
        continue;
      }

      // Hotel previous-stay rating line: "Name — Overall: X/5, Rooms: X/5, ..."
      // Renders as an expandable summary with sub-ratings in a collapsible breakdown.
      const hotelRatingMatch = trimmed.match(
        /^(.+?)\s+(?:—|–|-|:)\s*Overall:\s*(\d)\/5,\s*Rooms:\s*(\d)\/5,\s*Value:\s*(\d)\/5,\s*Service:\s*(\d)\/5,\s*Cleanliness:\s*(\d)\/5,\s*Facilities:\s*(\d)\/5$/i
      );
      if (hotelRatingMatch) {
        const [, name, overall, rooms, value, service, cleanliness, facilities] = hotelRatingMatch;
        html.push(
          `<details class="chat-hotel-stay-rating">` +
            `<summary>` +
            `<strong class="chat-hotel-stay-rating__name">${this.inlineFormat(name)}</strong>` +
            `<span class="chat-hotel-stay-rating__overall">Overall: ${overall}/5 <span class="chat-hotel-stay-rating__toggle">see breakdown</span></span>` +
            `</summary>` +
            `<ul>` +
            `<li>Rooms: ${rooms}/5</li>` +
            `<li>Value: ${value}/5</li>` +
            `<li>Service: ${service}/5</li>` +
            `<li>Cleanliness: ${cleanliness}/5</li>` +
            `<li>Facilities: ${facilities}/5</li>` +
            `</ul>` +
            `</details>`
        );
        continue;
      }

      // Regular paragraph
      html.push(`<p>${this.inlineFormat(trimmed)}</p>`);
    }

    if (inList) {
      html.push('</ul>');
    }
    if (inOrderedList) {
      html.push('</ol>');
    }

    return html.join('');
  }

  // ENT-16993 — escape the HTML-significant chars; '&' first so we don't
  // re-escape the entities we add. Markdown chars (* # - [ ]) are untouched.
  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private inlineFormat(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )
      .replace(
        /(?<!['"=])(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      );
  }

  private scrollToBottom(): void {
    // ENT-16431 — defer a tick: newly-mounted cards (e.g. the MI recap, which
    // can be considerably taller than a plain text bubble) don't always have
    // their final layout height settled within the same ngAfterViewChecked
    // pass, so reading scrollHeight synchronously here could under-scroll.
    setTimeout(() => {
      if (this.messageList?.nativeElement) {
        const el = this.messageList.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  /**
   * ENT-16247 — once the feedback reason/comment panel renders, make sure the
   * whole form is visible. When the rated bubble sits at the very bottom of
   * the scroll area the newly-expanded panel falls below the fold, so the user
   * had to scroll manually to reach the chips and Submit/Skip buttons.
   */
  private scrollFeedbackIntoView(): void {
    setTimeout(() => {
      const list = this.messageList?.nativeElement as HTMLElement | undefined;
      const panel = list?.querySelector('.chat-widget-feedback-detail') as HTMLElement | null;
      panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  private cacheBookingSelectionFlags(message: ChatMessage): void {
    if (!message.id) {
      return;
    }
    const serviceTypes =
      message.bookingSummary?.items?.map((item) => item.serviceType?.toUpperCase()) ?? [];
    this.bookingSelectionFlags[message.id] = {
      isFlight: serviceTypes.includes(ServiceType.Flight),
      isRail: serviceTypes.includes(ServiceType.Rail)
    };
  }

  getIconFromServiceType(itemServiceType: string | undefined): IconDefinition {
    switch (itemServiceType?.toUpperCase()) {
      case ServiceType.Hotel:
        return faHotel;
      case ServiceType.Car:
        return faCarSide;
      case ServiceType.Flight:
        return faPlaneDeparture;
      case ServiceType.Lounge:
        return faCocktail;
      case ServiceType.Rail:
        return faTrain;
      default:
        return faBasketShopping;
    }
  }

  getProductText(item: BookingSummaryItem): string {
    switch (item.serviceType?.toUpperCase()) {
      case ServiceType.Hotel:
        return item.bookingName || item.serviceName || 'Hotel';
      case ServiceType.Car:
        return item.bookingName || item.serviceName || 'Rental Car';
      case ServiceType.Lounge:
        return item.bookingName || item.serviceName || 'Lounge';
      case ServiceType.Flight:
        return 'Flight';
      case ServiceType.Rail:
        return item.bookingName || item.serviceName || 'Train';
      case ServiceType.Parking:
        return item.bookingName || item.serviceName || 'Airport Parking';
      case ServiceType.FastTrack:
        return item.bookingName || item.serviceName || 'Fast Track';
      case ServiceType.Eurostar:
        return item.bookingName || item.serviceName || 'Eurostar';
      default:
        return item.bookingName || item.serviceName || 'Product';
    }
  }

  getBookingDateText(item: BookingSummaryItem): string {
    const outboundDate = this.formatBookingDate(item.outboundDate);
    const inboundDate = item.inboundDate ? this.formatBookingDate(item.inboundDate) : '';

    return !inboundDate ||
      this.bookingDateKey(item.outboundDate) === this.bookingDateKey(item.inboundDate)
      ? outboundDate
      : `${outboundDate} - ${inboundDate}`;
  }

  getListCarriersText(item: BookingSummaryItem): string {
    if (item.serviceType !== ServiceType.Rail && item.serviceType !== ServiceType.Flight) {
      return '';
    }
    const carriers = item.segments?.flatMap((segment) =>
      'carrier' in segment && typeof segment.carrier === 'string' ? [segment.carrier] : []
    );
    const carrierSet = new Set(carriers);
    return Array.from(carrierSet).join(' / ');
  }

  private formatBookingDate(date: string | undefined): string {
    if (!date) {
      return '';
    }

    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    }).format(new Date(date));
  }

  private bookingDateKey(date: string | undefined): string {
    return date ? new Date(date).toLocaleDateString() : '';
  }

  private cacheBookingSummaryDisplay(message: ChatMessage): void {
    const items = [
      ...(message.bookingSummary?.items ?? []),
      ...(message.bookingResult?.summary?.items ?? [])
    ];

    items.forEach((item) => {
      this.bookingSummaryDisplay[item.id] = {
        icon: this.getIconFromServiceType(item.serviceType),
        productText: this.getProductText(item),
        dateText: this.getBookingDateText(item),
        listCarriers: this.getListCarriersText(item)
      };
    });
  }
}
